import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect, lazy, Suspense } from "react";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { AnalyticsWrapper } from "./components/AnalyticsWrapper";
import { MetaTags } from "./components/MetaTags";

// Lazy load pages that aren't needed on initial load
const Waitlist = lazy(() => import("./pages/Waitlist"));
const Zaposlitev = lazy(() => import("./pages/Zaposlitev"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const Freebies = lazy(() => import("./pages/Freebies"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const SalaryCalculatorPage = lazy(() => import("./pages/SalaryCalculatorPage"));
const BurnRateCalculatorPage = lazy(() => import("./pages/BurnRateCalculatorPage"));
const NormiranecCalculatorPage = lazy(() => import("./pages/NormiranecCalculatorPage"));
const NormiranecCalculatorPage2025 = lazy(() => import("./pages/NormiranecCalculatorPage2025"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Cookies = lazy(() => import("./pages/Cookies"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const RSSRedirect = lazy(() => import("./pages/RSSRedirect"));
const PackagesPage = lazy(() => import("./pages/PackagesPage"));
const CenikPage = lazy(() => import("./pages/Cenik"));
const CPACalculatorPage = lazy(() => import("./pages/CPACalculatorPage"));

// Admin pages - lazy loaded
const Login = lazy(() => import("./pages/admin/Login"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const ContactSubmissionDetail = lazy(() => import("./pages/admin/ContactSubmissionDetail"));
const BlogPreview = lazy(() => import("./pages/admin/BlogPreview"));
const ProtectedRoute = lazy(() => import("./components/admin/ProtectedRoute"));

// Loading fallback for lazy components
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);


const queryClient = new QueryClient();

// Scroll to top on route change
function ScrollToTop() {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  return null;
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <MetaTags />
        <BrowserRouter>
          <AnalyticsWrapper />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/cakalna-vrsta" element={<Waitlist />} />
              <Route path="/zaposlitev" element={<Zaposlitev />} />
              <Route path="/zaposlitev/:jobId" element={<JobDetail />} />
              <Route path="/priboljski" element={<Freebies />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/bruto-neto-kalkulator" element={<SalaryCalculatorPage />} />
              <Route path="/burn-rate" element={<BurnRateCalculatorPage />} />
              <Route path="/normiranec-2025" element={<NormiranecCalculatorPage2025 />} />
              <Route path="/normiranec-2026" element={<NormiranecCalculatorPage />} />
              <Route path="/pogoji-uporabe" element={<TermsOfService />} />
              <Route path="/zasebnost" element={<Privacy />} />
              <Route path="/piskotki" element={<Cookies />} />
              <Route path="/hvala" element={<ThankYou />} />
              <Route path="/blog-rss" element={<RSSRedirect />} />
              <Route path="/paketi" element={<PackagesPage />} />
              <Route path="/cenik" element={<CenikPage />} />
              <Route path="/cpa-kalkulator" element={<CPACalculatorPage />} />
              
              <Route path="/admin/login" element={<Login />} />
              <Route path="/admin" element={<Suspense fallback={<PageLoader />}><ProtectedRoute><Dashboard /></ProtectedRoute></Suspense>} />
              <Route path="/admin/povprasevanja/:id" element={<Suspense fallback={<PageLoader />}><ProtectedRoute><ContactSubmissionDetail /></ProtectedRoute></Suspense>} />
              <Route path="/admin/blog-preview/:slug" element={<Suspense fallback={<PageLoader />}><ProtectedRoute><BlogPreview /></ProtectedRoute></Suspense>} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
